/**
 * @file shipping.store.ts
 *
 * Zustand store for the Shipping tab. Bespoke (it does NOT compose
 * {@link createFormSlice}): a custom shipping zone owns its own nested methods and
 * locations, and local pickups live on a separate shop-settings surface, so
 * the cache-first CRUD-form slice doesn't fit. It pairs the zone list (with its
 * per-zone method and location maps) and the geographic reference data with the
 * pickup-locations settings, and exposes the granular mutations the overview
 * (reorder, delete) and the routed forms (create/update zone, sync methods and
 * locations, save pickups) call. Reorder is optimistic; mutations return
 * `true`/`false` (or the entity) and surface failures via `error` / `toMessage`.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { loadContinents } from '../shipping/region-data';
import {
  fetchShippingZones,
  fetchShippingZoneMethods,
  createShippingZone,
  updateShippingZoneName,
  reorderShippingZones,
  deleteShippingZone,
  setShippingZoneLocations,
  addShippingZoneMethod,
  updateShippingZoneMethod,
  deleteShippingZoneMethod,
} from '../api/shipping';
import {
  fetchPickupLocations,
  createPickupLocation,
  updatePickupLocation,
  deletePickupLocation,
  reorderPickupLocations,
} from '../api/pickup-locations';
import type {
  ShippingMethod,
  ShippingZone,
  ShippingZoneLocation,
  ShippingZoneMethodUpdate,
  ShopContinent,
  ShopPickupLocation,
  PickupLocationInput,
} from '../api/shop-types';
import { toMessage } from './store-utils';

/**
 * Shape of the shipping store: the zones (with per-zone method/location maps),
 * the region reference tree, and the shop settings holding pickup locations,
 * plus the granular zone/method/location/pickup mutations.
 */
interface ShippingState {
  /** The shipping zones, in display order (`order` ascending). */
  zones: ShippingZone[];
  /** Locations keyed by zone id. */
  zoneLocations: Record<string, ShippingZoneLocation[]>;
  /** Methods keyed by zone id. */
  zoneMethods: Record<string, ShippingMethod[]>;
  /** The region tree (continents → countries → states). */
  continents: ShopContinent[];
  /** The pickup locations, in display order (`sortOrder` ascending). */
  pickupLocations: ShopPickupLocation[];
  /** Whether the zones fetch is in flight. */
  loading: boolean;
  /** Last zones-fetch / mutation error message, or `null`. */
  error: string | null;
  /** Whether the pickup-locations fetch is in flight. */
  pickupLoading: boolean;
  /** Last pickup-locations fetch error, or `null`. */
  pickupError: string | null;

  /** Fetches zones plus their methods/locations and the region tree. */
  fetchShipping: () => Promise<void>;
  /** Fetches only the region tree (for a form deep-link). */
  fetchContinents: () => Promise<void>;
  /** Fetches the shop's pickup locations from `/v1`. */
  fetchPickupLocations: () => Promise<void>;

  /**
   * Optimistically reorders the zones to the given id order and persists each
   * zone's new position.
   *
   * @param orderedZoneIds - The zone ids in the desired display order.
   *
   * @returns `true` on success; reverts and returns `false` on failure.
   */
  reorderZones: (orderedZoneIds: string[]) => Promise<boolean>;

  /**
   * Creates a zone (with empty methods/locations).
   *
   * @param name - The zone name.
   *
   * @returns The created zone, or `null` on failure.
   */
  createZone: (name: string) => Promise<ShippingZone | null>;

  /**
   * Renames a zone.
   *
   * @param id - The zone id.
   * @param name - The new name.
   *
   * @returns `true` on success, `false` on failure.
   */
  updateZone: (id: string, name: string) => Promise<boolean>;

  /**
   * Deletes a zone and forgets its methods/locations.
   *
   * @param id - The zone id.
   *
   * @returns `true` on success, `false` on failure.
   */
  deleteZone: (id: string) => Promise<boolean>;

  /**
   * Replaces a zone's full set of regions.
   *
   * @param zoneId - The zone id.
   * @param locations - The complete desired location set.
   *
   * @returns `true` on success, `false` on failure.
   */
  setZoneLocations: (
    zoneId: string,
    locations: ShippingZoneLocation[],
  ) => Promise<boolean>;

  /**
   * Adds a method to a zone.
   *
   * @param zoneId - The zone id.
   * @param methodId - The method type (`"flat_rate"` / `"free_shipping"`).
   *
   * @returns The created method, or `null` on failure.
   */
  addZoneMethod: (
    zoneId: string,
    methodId: string,
  ) => Promise<ShippingMethod | null>;

  /**
   * Updates a method on a zone.
   *
   * @param zoneId - The zone id.
   * @param instanceId - The method instance id.
   * @param data - The method update (enabled/title/settings).
   *
   * @returns `true` on success, `false` on failure.
   */
  updateZoneMethod: (
    zoneId: string,
    instanceId: string,
    data: ShippingZoneMethodUpdate,
  ) => Promise<boolean>;

  /**
   * Deletes a method from a zone.
   *
   * @param zoneId - The zone id.
   * @param instanceId - The method instance id.
   *
   * @returns `true` on success, `false` on failure.
   */
  deleteZoneMethod: (zoneId: string, instanceId: string) => Promise<boolean>;

  /**
   * Creates a pickup location, appended at the end of the current order.
   *
   * @param input - The editable fields.
   *
   * @returns The created location, or `null` on failure.
   */
  createPickup: (input: PickupLocationInput) => Promise<ShopPickupLocation | null>;

  /**
   * Updates a pickup location's editable fields.
   *
   * @param id - The pickup-location id.
   * @param input - The editable fields.
   *
   * @returns `true` on success, `false` on failure.
   */
  updatePickup: (id: string, input: PickupLocationInput) => Promise<boolean>;

  /**
   * Deletes a pickup location, removing it from the list on success.
   *
   * @param id - The pickup-location id.
   *
   * @returns `true` on success, `false` on failure.
   */
  deletePickup: (id: string) => Promise<boolean>;

  /**
   * Optimistically reorders the pickup locations to the given id order and persists
   * each one's new position.
   *
   * @param orderedIds - The pickup-location ids in the desired display order.
   *
   * @returns `true` on success; reverts and returns `false` on failure.
   */
  reorderPickups: (orderedIds: string[]) => Promise<boolean>;
}

export const useShippingStore = create<ShippingState>()(
  devtools(
    (set, get) => ({
      zones: [],
      zoneLocations: {},
      zoneMethods: {},
      continents: [],
      pickupLocations: [],
      loading: false,
      error: null,
      pickupLoading: false,
      pickupError: null,

      fetchShipping: async () => {
        set({ loading: true, error: null }, false, 'shipping/fetch/pending');
        try {
          const [zonesWithLocations, continents] = await Promise.all([
            fetchShippingZones(),
            loadContinents(),
          ]);
          const sorted = [...zonesWithLocations].sort(
            (a, b) => a.zone.order - b.zone.order,
          );
          const methodLists = await Promise.all(
            sorted.map((entry) => fetchShippingZoneMethods(entry.zone.id)),
          );
          const zoneLocations: Record<string, ShippingZoneLocation[]> = {};
          const zoneMethods: Record<string, ShippingMethod[]> = {};
          sorted.forEach((entry, index) => {
            zoneLocations[entry.zone.id] = entry.locations;
            zoneMethods[entry.zone.id] = methodLists[index];
          });
          set(
            {
              zones: sorted.map((entry) => entry.zone),
              zoneLocations,
              zoneMethods,
              continents,
              loading: false,
            },
            false,
            'shipping/fetch/fulfilled',
          );
        } catch (err) {
          set(
            { loading: false, error: toMessage(err) },
            false,
            'shipping/fetch/rejected',
          );
        }
      },

      fetchContinents: async () => {
        try {
          const continents = await loadContinents();
          set({ continents }, false, 'shipping/continents/fulfilled');
        } catch (err) {
          set({ error: toMessage(err) }, false, 'shipping/continents/rejected');
        }
      },

      fetchPickupLocations: async () => {
        set(
          { pickupLoading: true, pickupError: null },
          false,
          'shipping/pickup/pending',
        );
        try {
          const pickupLocations = await fetchPickupLocations();
          set(
            { pickupLocations, pickupLoading: false },
            false,
            'shipping/pickup/fulfilled',
          );
        } catch (err) {
          set(
            { pickupLoading: false, pickupError: toMessage(err) },
            false,
            'shipping/pickup/rejected',
          );
        }
      },

      reorderZones: async (orderedZoneIds) => {
        const previous = get().zones;
        const byId = new Map(previous.map((zone) => [zone.id, zone]));
        const reordered = orderedZoneIds
          .map((id, index) => {
            const zone = byId.get(id);
            return zone ? { ...zone, order: index } : null;
          })
          .filter((zone): zone is ShippingZone => zone !== null);
        set({ zones: reordered }, false, 'shipping/reorderZones/optimistic');
        try {
          await reorderShippingZones(
            reordered.map((zone) => ({ id: zone.id, sortOrder: zone.order })),
          );
          return true;
        } catch (err) {
          set(
            { zones: previous, error: toMessage(err) },
            false,
            'shipping/reorderZones/rejected',
          );
          return false;
        }
      },

      createZone: async (name) => {
        try {
          // Append at the end: the next position after the current highest order — mirrors how
          // pickup locations are created, so a new zone holds a distinct sort_order instead of
          // piling up at the default 0 (which makes zones sort by random id, not creation order).
          const zone = await createShippingZone(name, get().zones.length);
          set(
            (state) => ({
              zones: [...state.zones, zone],
              zoneLocations: { ...state.zoneLocations, [zone.id]: [] },
              zoneMethods: { ...state.zoneMethods, [zone.id]: [] },
            }),
            false,
            'shipping/createZone/fulfilled',
          );
          return zone;
        } catch (err) {
          set({ error: toMessage(err) }, false, 'shipping/createZone/rejected');
          return null;
        }
      },

      updateZone: async (id, name) => {
        try {
          const zone = await updateShippingZoneName(id, name);
          set(
            (state) => ({
              zones: state.zones.map((z) =>
                z.id === id ? { ...z, name: zone.name } : z,
              ),
            }),
            false,
            'shipping/updateZone/fulfilled',
          );
          return true;
        } catch (err) {
          set({ error: toMessage(err) }, false, 'shipping/updateZone/rejected');
          return false;
        }
      },

      deleteZone: async (id) => {
        try {
          await deleteShippingZone(id);
          set(
            (state) => {
              const zoneLocations = { ...state.zoneLocations };
              const zoneMethods = { ...state.zoneMethods };
              delete zoneLocations[id];
              delete zoneMethods[id];
              return {
                zones: state.zones.filter((z) => z.id !== id),
                zoneLocations,
                zoneMethods,
              };
            },
            false,
            'shipping/deleteZone/fulfilled',
          );
          return true;
        } catch (err) {
          set({ error: toMessage(err) }, false, 'shipping/deleteZone/rejected');
          return false;
        }
      },

      setZoneLocations: async (zoneId, locations) => {
        try {
          const saved = await setShippingZoneLocations(zoneId, locations);
          set(
            (state) => ({
              zoneLocations: { ...state.zoneLocations, [zoneId]: saved },
            }),
            false,
            'shipping/setZoneLocations/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { error: toMessage(err) },
            false,
            'shipping/setZoneLocations/rejected',
          );
          return false;
        }
      },

      addZoneMethod: async (zoneId, methodId) => {
        try {
          const method = await addShippingZoneMethod(zoneId, methodId);
          set(
            (state) => ({
              zoneMethods: {
                ...state.zoneMethods,
                [zoneId]: [...(state.zoneMethods[zoneId] ?? []), method],
              },
            }),
            false,
            'shipping/addZoneMethod/fulfilled',
          );
          return method;
        } catch (err) {
          set({ error: toMessage(err) }, false, 'shipping/addZoneMethod/rejected');
          return null;
        }
      },

      updateZoneMethod: async (zoneId, instanceId, data) => {
        try {
          const method = await updateShippingZoneMethod(zoneId, instanceId, data);
          set(
            (state) => ({
              zoneMethods: {
                ...state.zoneMethods,
                [zoneId]: (state.zoneMethods[zoneId] ?? []).map((m) =>
                  m.instance_id === instanceId ? method : m,
                ),
              },
            }),
            false,
            'shipping/updateZoneMethod/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { error: toMessage(err) },
            false,
            'shipping/updateZoneMethod/rejected',
          );
          return false;
        }
      },

      deleteZoneMethod: async (zoneId, instanceId) => {
        try {
          await deleteShippingZoneMethod(zoneId, instanceId);
          set(
            (state) => ({
              zoneMethods: {
                ...state.zoneMethods,
                [zoneId]: (state.zoneMethods[zoneId] ?? []).filter(
                  (m) => m.instance_id !== instanceId,
                ),
              },
            }),
            false,
            'shipping/deleteZoneMethod/fulfilled',
          );
          return true;
        } catch (err) {
          set(
            { error: toMessage(err) },
            false,
            'shipping/deleteZoneMethod/rejected',
          );
          return false;
        }
      },

      createPickup: async (input) => {
        try {
          // Append at the end: the next position after the current highest sortOrder.
          const sortOrder = get().pickupLocations.length;
          const created = await createPickupLocation(input, sortOrder);
          set(
            (state) => ({ pickupLocations: [...state.pickupLocations, created] }),
            false,
            'shipping/createPickup/fulfilled',
          );
          return created;
        } catch (err) {
          set({ pickupError: toMessage(err) }, false, 'shipping/createPickup/rejected');
          return null;
        }
      },

      updatePickup: async (id, input) => {
        try {
          const updated = await updatePickupLocation(id, input);
          set(
            (state) => ({
              pickupLocations: state.pickupLocations.map((p) => (p.id === id ? updated : p)),
            }),
            false,
            'shipping/updatePickup/fulfilled',
          );
          return true;
        } catch (err) {
          set({ pickupError: toMessage(err) }, false, 'shipping/updatePickup/rejected');
          return false;
        }
      },

      deletePickup: async (id) => {
        try {
          await deletePickupLocation(id);
          set(
            (state) => ({
              pickupLocations: state.pickupLocations.filter((p) => p.id !== id),
            }),
            false,
            'shipping/deletePickup/fulfilled',
          );
          return true;
        } catch (err) {
          set({ pickupError: toMessage(err) }, false, 'shipping/deletePickup/rejected');
          return false;
        }
      },

      reorderPickups: async (orderedIds) => {
        const previous = get().pickupLocations;
        const byId = new Map(previous.map((p) => [p.id, p]));
        const reordered = orderedIds
          .map((id, index) => {
            const pickup = byId.get(id);
            return pickup ? { ...pickup, sortOrder: index } : null;
          })
          .filter((p): p is ShopPickupLocation => p !== null);
        set({ pickupLocations: reordered }, false, 'shipping/reorderPickups/optimistic');
        try {
          await reorderPickupLocations(
            reordered.map((p) => ({ id: p.id, sortOrder: p.sortOrder })),
          );
          return true;
        } catch (err) {
          set(
            { pickupLocations: previous, pickupError: toMessage(err) },
            false,
            'shipping/reorderPickups/rejected',
          );
          return false;
        }
      },
    }),
    { name: 'shop/shipping' },
  ),
);
